import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar, MapPin, Clock } from "lucide-react";
import api from "@/lib/api";
import useFreshData from "@/lib/useFreshData";
import SEO, { breadcrumbSchema } from "@/components/seo/SEO";

function formatEventDate(iso) {
    try {
        const d = new Date(iso + "T00:00:00");
        return {
            day: d.toLocaleDateString("es-ES", { day: "2-digit" }),
            month: d.toLocaleDateString("es-ES", { month: "short" }).replace(".", "").toUpperCase(),
            year: d.toLocaleDateString("es-ES", { year: "numeric" }),
            weekday: d.toLocaleDateString("es-ES", { weekday: "long" }),
        };
    } catch {
        return { day: "", month: "", year: "", weekday: "" };
    }
}

export default function Events() {
    const [filter, setFilter] = useState("upcoming");
    const { data: items, loading } = useFreshData(
        async () => {
            const r = await api.get("/events", {
                params: filter === "upcoming" ? { upcoming: true } : {},
            });
            return r.data || [];
        },
        { initialData: [], deps: [filter] }
    );
    const list = items || [];

    return (
        <div data-testid="events-page" className="bg-white">
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-10 sm:pt-24">
                <div className="text-xs uppercase tracking-widest text-[#082050]/70 mb-4">
                    Eventos
                </div>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#082050] tracking-tight">
                    Agenda de ASATTA.
                </h1>
                <p className="mt-4 text-lg text-[#4A5568] max-w-2xl">
                    Asambleas, jornadas, formaciones y ferias organizadas por la
                    asociación y sus socios.
                </p>
            </section>

            {/* Evento destacado: I Jornada de Networking */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-4">
                <Link
                    to="/eventos/jornada-networking"
                    data-testid="events-featured-jornada"
                    className="group block border border-[#082050] bg-[#082050] text-white overflow-hidden"
                >
                    <div className="grid grid-cols-1 md:grid-cols-[140px_1fr_auto] gap-6 md:gap-8 items-center p-6 sm:p-8">
                        <div className="text-center md:text-left leading-none">
                            <div className="text-xs uppercase tracking-widest text-white/60">Sep</div>
                            <div className="text-5xl sm:text-6xl font-semibold mt-1">30</div>
                            <div className="text-xs text-white/60 mt-2">Miércoles · 9:30–13:30</div>
                        </div>
                        <div>
                            <div className="text-xs uppercase tracking-widest text-white/60 mb-2">
                                Evento destacado
                            </div>
                            <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-white">
                                I Jornada Profesional & Networking
                            </h2>
                            <p className="mt-3 text-sm sm:text-base text-white/75 leading-relaxed max-w-xl">
                                Dos mesas redondas sobre experiencias y estrategias
                                de destinos, con la presencia del Patronato. En
                                Dunas Tarifa, Valdevaqueros.
                            </p>
                        </div>
                        <div className="justify-self-start md:justify-self-end inline-flex items-center gap-2 bg-white text-[#082050] px-6 py-3 text-sm font-medium group-hover:bg-white/90">
                            Ver detalles
                            <ArrowRight size={14} />
                        </div>
                    </div>
                </Link>
            </section>

            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-6">
                <div className="flex flex-wrap gap-2 border-t border-b border-[#E2E4E9] py-4">
                    {[
                        { id: "upcoming", label: "Próximos" },
                        { id: "all", label: "Todos" },
                    ].map((f) => (
                        <button
                            key={f.id}
                            onClick={() => setFilter(f.id)}
                            data-testid={`events-filter-${f.id}`}
                            className={`text-xs uppercase tracking-widest px-4 py-2 border transition-colors ${
                                filter === f.id
                                    ? "bg-[#082050] text-white border-[#082050]"
                                    : "border-[#E2E4E9] text-[#4A5568] hover:border-[#082050]"
                            }`}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 pb-24">
                {loading && (
                    <div data-testid="events-loading" className="py-20 text-center text-[#4A5568]">
                        Cargando eventos...
                    </div>
                )}
                {!loading && list.length === 0 && (
                    <div data-testid="events-empty" className="py-20 text-center text-[#4A5568]">
                        No hay eventos {filter === "upcoming" ? "próximos" : "en la agenda"}.
                    </div>
                )}
                <div className="space-y-6">
                    {list.map((e, i) => {
                        const d = formatEventDate(e.event_date);
                        return (
                            <article
                                key={e.id || i}
                                data-testid={`event-item-${i}`}
                                className="border border-[#E2E4E9] hover:border-[#082050] transition-colors flex flex-col md:flex-row"
                            >
                                {e.image && (
                                    <div className="relative md:w-72 aspect-[16/10] md:aspect-auto overflow-hidden bg-[#F8F9FA]">
                                        <img
                                            src={e.image}
                                            alt={`Imagen del evento: ${e.title}`}
                                            loading="lazy"
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                )}
                                <div className="p-6 sm:p-8 flex-1 flex flex-col md:flex-row md:gap-8">
                                    <div className="md:w-32 md:shrink-0 mb-4 md:mb-0">
                                        <div className="border-l-2 border-[#082050] pl-3">
                                            <div className="text-3xl font-semibold text-[#082050] leading-none">
                                                {d.day}
                                            </div>
                                            <div className="text-xs uppercase tracking-widest text-[#082050]/60 mt-1">
                                                {d.month} {d.year}
                                            </div>
                                            <div className="text-xs text-[#718096] mt-1 capitalize">
                                                {d.weekday}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="text-xl font-semibold text-[#082050] tracking-tight">
                                            {e.title}
                                        </h3>
                                        <p className="mt-3 text-sm text-[#4A5568] leading-relaxed">
                                            {e.description}
                                        </p>
                                        <div className="mt-4 flex flex-wrap gap-4 text-xs text-[#4A5568]">
                                            {e.event_time && (
                                                <span className="inline-flex items-center gap-1">
                                                    <Clock size={12} /> {e.event_time}
                                                </span>
                                            )}
                                            {e.location && (
                                                <span className="inline-flex items-center gap-1">
                                                    <MapPin size={12} /> {e.location}
                                                </span>
                                            )}
                                        </div>
                                        {e.cta_url && e.cta_label && (
                                            <a
                                                href={e.cta_url}
                                                data-testid={`event-item-cta-${i}`}
                                                className="mt-6 inline-flex items-center gap-2 text-xs font-medium text-white bg-[#082050] px-4 py-2 hover:bg-[#082050]/90"
                                            >
                                                {e.cta_label}
                                                <ArrowRight size={12} />
                                            </a>
                                        )}
                                    </div>
                                </div>
                            </article>
                        );
                    })}
                </div>
            </section>
        </div>
    );
}
